import { apiClient, apiGet, apiPost } from './client';
import type { CreateOrderRequest, Order, OrderStatus, Page, PageParams } from '@/types';

export interface OrderListParams extends PageParams {
  status?: OrderStatus;
  statuses?: OrderStatus[];
  retailerId?: number;
  distributorId?: number;
  partyTypes?: string[];
  repIds?: number[];
  partyName?: string;
  partyPhone?: string;
  orderNumber?: string;
  productQuery?: string;
  /** My Orders sidebar drill-down — restrict to this one supplier (distributor) tenant. */
  supplierTenantId?: number;
  /** My Orders sidebar drill-down — restrict to orders containing this exact product name. */
  exactProductName?: string;
  /** My Orders "Schemes" quick filter — only orders with a free-qty item. */
  schemesOnly?: boolean;
  /** My Orders "Shortages" quick filter — only orders with a recorded shortage. */
  hasShortages?: boolean;
  /** My Orders "Rep Orders" quick filter — only orders placed via any sales rep. */
  anyRep?: boolean;
  /** My Orders free-text search box — matches order number, supplier name or product name/SKU. */
  q?: string;
  startDate?: string;
  endDate?: string;
}

export interface ShortageEntry {
  sku: string;
  productName: string;
  orderedQty: number;
  receivedQty: number;
  shortageQty: number;
}

export interface OrderShortageRecord extends ShortageEntry {
  orderId: number;
}

export const ordersApi = {
  list(params: OrderListParams = {}) {
    return apiGet<Page<Order>>('/orders', { params });
  },

  get(id: number) {
    return apiGet<Order>(`/orders/${id}`);
  },

  place(req: CreateOrderRequest) {
    return apiPost<Order, CreateOrderRequest>('/orders', req);
  },

  confirm(id: number) {
    return apiPost<Order, undefined>(`/orders/${id}/confirm`);
  },

  cancel(id: number, reason?: string) {
    return apiPost<Order, { reason?: string }>(`/orders/${id}/cancel`, { reason });
  },

  view(id: number) {
    return apiPost<Order, undefined>(`/orders/${id}/view`);
  },

  reject(id: number, reason: string) {
    return apiPost<Order, { reason: string }>(`/orders/${id}/reject`, { reason });
  },

  complete(id: number) {
    return apiPost<Order, undefined>(`/orders/${id}/complete`);
  },

  getShortages(id: number) {
    return apiGet<ShortageEntry[]>(`/orders/${id}/shortages`);
  },

  recordShortages(id: number, shortages: ShortageEntry[]) {
    return apiPost<ShortageEntry[], { shortages: ShortageEntry[] }>(`/orders/${id}/shortages`, { shortages });
  },

  getShortagesForOrders(orderIds: number[]) {
    if (orderIds.length === 0) return Promise.resolve([] as OrderShortageRecord[]);
    return apiGet<OrderShortageRecord[]>('/orders/shortages', { params: { orderIds } });
  },

  uploadInvoice(id: number, file: File) {
    const form = new FormData();
    form.append('file', file);
    // Don't set Content-Type explicitly — the axios instance defaults it to
    // application/json, which would override FormData's auto-generated boundary
    // and break the server's multipart parsing. Unsetting it here lets the
    // browser set "multipart/form-data; boundary=..." itself.
    return apiPost<Order, FormData>(`/orders/${id}/invoice`, form, {
      headers: { 'Content-Type': undefined },
    });
  },

  async downloadInvoice(id: number): Promise<Blob> {
    const resp = await apiClient.get(`/orders/${id}/invoice`, { responseType: 'blob' });
    return resp.data as Blob;
  },
};
